import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '../context/LanguageContext';
import HERO_PRODUCT_IMG from '../assets/scribai-hero-cutout.png';
import LOVE_IT_BANNER from '../assets/scribai-love-it-banner.jpg';
import SmartSummariesSection from '../components/SmartSummariesSection';

gsap.registerPlugin(ScrollTrigger);

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_5a0445d2-18b0-46bc-93e5-7d48b61be08b/artifacts/gsnaomur_Logo%20Sin%20texto%20copy.png";
const VISION_IMG = "https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/geqamjbt_ScribAI_With_Phone_App.png";

const heroWordClass = "text-[clamp(1.85rem,9vw,7.5rem)] leading-[0.88] font-semibold tracking-tight text-[var(--text-primary)]";

// Reveal-on-scroll driven by a plain IntersectionObserver + CSS transition (see .reveal /
// .reveal-visible in index.css). Deliberately not Framer Motion's initial/animate or
// whileInView here: both were observed to intermittently stick at their initial (invisible)
// state on first paint for this page. Elements already in the viewport at mount reveal
// immediately instead of waiting on the observer's first async callback.
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Scroll-scrubbed parallax on the section images. Uses GSAP's ScrollTrigger (scrub: true ties
// the tween directly to scroll position) instead of Framer Motion's useScroll/useTransform —
// same reasoning as the hero reveal: fewer library-internal behaviors to reason about here.
const ParallaxImage = ({ src, alt, className = '' }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        imgRef.current,
        { yPercent: -8 },
        {
          yPercent: 8,
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className={`relative overflow-hidden rounded-2xl glass-card ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="w-full h-full object-cover scale-110"
        loading="lazy"
      />
    </div>
  );
};

const AnnotationTag = ({ label, position, delay }) => (
  <Reveal delay={delay} className={`hidden md:block absolute ${position}`}>
    <div className="font-technical text-[11px] uppercase border border-[var(--border-subtle)] bg-[var(--bg-page)]/70 backdrop-blur-sm px-3 py-1.5 text-[var(--text-primary)] whitespace-nowrap">
      {label}
    </div>
  </Reveal>
);

// Word-by-word / char-by-char hero headline reveal. Chars are split manually at render time
// (not via the SplitText plugin) and animated with gsap.fromTo for determinism — no plugin
// lifecycle to reason about, the char spans are just always in the JSX. Runs once on mount
// (above-the-fold, always in view at load) rather than on scroll.
const SplitHeadlineWord = ({ text, className, delay = 0 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const chars = ref.current.querySelectorAll('.char-inner');
      gsap.fromTo(
        chars,
        { yPercent: 110, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.9, ease: 'power4.out', stagger: 0.025, delay }
      );
    }, ref);
    return () => ctx.revert();
  }, [text, delay]);

  return (
    <h1 ref={ref} className={className} aria-label={text}>
      {text.split('').map((ch, i) => (
        <span key={i} className="inline-block overflow-hidden" aria-hidden="true">
          <span className="char-inner inline-block">{ch === ' ' ? ' ' : ch}</span>
        </span>
      ))}
    </h1>
  );
};

// Counts up from 0 to `value` once the element scrolls into view. Runs once (ScrollTrigger
// `once: true`) rather than repeating on every re-entry, matching Reveal's one-shot behavior.
const CountUpStat = ({ value, decimals = 0, suffix = '', className }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const proxy = { val: 0 };
      gsap.to(proxy, {
        val: value,
        duration: 1.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 85%',
          once: true,
        },
        onUpdate: () => {
          if (ref.current) ref.current.textContent = proxy.val.toFixed(decimals) + suffix;
        },
      });
    }, ref);
    return () => ctx.revert();
  }, [value, decimals, suffix]);

  return (
    <p ref={ref} className={className}>
      0{suffix}
    </p>
  );
};

const BrandIntroPage = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="brand-intro-page" className="min-h-screen bg-[var(--bg-page)]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden px-6 sm:px-10 pt-28 pb-10">
        <span className="corner-bracket corner-bracket--tl hidden sm:block" />
        <span className="corner-bracket corner-bracket--tr hidden sm:block" />
        <span className="corner-bracket corner-bracket--bl hidden sm:block" />
        <span className="corner-bracket corner-bracket--br hidden sm:block" />

        {/* Ambient breathing glow behind the product */}
        <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
          <motion.div
            className="w-[65vw] h-[65vw] max-w-[820px] max-h-[820px] rounded-full bg-[var(--accent-link)]/20 blur-[130px]"
            animate={{ opacity: [0.45, 0.75, 0.45], scale: [1, 1.06, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Top row: two words */}
        <div className="flex items-start justify-between gap-4">
          <SplitHeadlineWord text={t('home.heroWord1')} className={heroWordClass} delay={0.1} />
          <SplitHeadlineWord text={t('home.heroWord2')} className={`${heroWordClass} text-right`} delay={0.25} />
        </div>

        {/* Center: product + recording accent + annotation tags */}
        <div className="relative flex-1 flex items-center justify-center py-10 min-h-[280px]">
          <div className="absolute top-0 sm:top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 font-technical text-xs text-[var(--accent-link)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
            <span>{t('home.heroRecording')}</span>
            <span className="flex items-end gap-0.5 h-3">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="w-0.5 bg-[var(--accent-link)] rounded-full"
                  style={{ height: '100%', animation: `waveform 1s ease-in-out ${i * 0.15}s infinite` }}
                />
              ))}
            </span>
          </div>

          <Reveal delay={0.4} className="relative w-[54vw] sm:w-[26vw] max-w-[300px]">
            <div className="hero-float">
              <img src={HERO_PRODUCT_IMG} alt="ScribAI Recorder" className="w-full drop-shadow-2xl" data-testid="hero-product-image" />
            </div>
            <div className="hero-float-shadow" />
          </Reveal>

          <AnnotationTag label={t('home.heroTag1')} position="left-[6%] top-[38%]" delay={0.7} />
          <AnnotationTag label={t('home.heroTag2')} position="right-[6%] bottom-[30%]" delay={0.85} />
        </div>

        {/* Bottom row: footnote + third word */}
        <div className="flex flex-col sm:flex-row items-end justify-between gap-8">
          <Reveal delay={0.3} className="max-w-xs order-2 sm:order-1">
            <div className="w-9 h-9 rounded-full border border-[var(--border-strong)] flex items-center justify-center mb-4">
              <img src={LOGO_ICON} className="w-4 h-4" alt="" />
            </div>
            <p className="font-technical text-[11px] uppercase leading-relaxed text-[var(--text-secondary)]">
              {t('home.heroFootnote')}
            </p>
          </Reveal>
          <div className="order-1 sm:order-2 w-full sm:w-auto">
            <SplitHeadlineWord text={t('home.heroWord3')} className={`${heroWordClass} text-right`} delay={0.4} />
          </div>
        </div>
      </section>

      {/* Vision — image left, text right */}
      <section className="py-24 md:py-32 px-6 sm:px-10 bg-[var(--bg-page)]">
        <div className="container-wide grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <ParallaxImage src={VISION_IMG} alt={t('home.visionImageAlt')} className="aspect-[4/5]" />
          </Reveal>
          <div>
            <Reveal>
              <p className="label-small mb-4">{t('home.ourVision')}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
                {t('home.visionTitle')}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-lg text-[var(--text-secondary)] leading-relaxed max-w-lg">
                {t('home.visionText')}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why You'll Love It — full-width promotional banner */}
      <section className="px-6 sm:px-10 pb-24 md:pb-32">
        <div className="container-wide">
          <Reveal>
            <div className="rounded-3xl overflow-hidden glass-card">
              <img
                src={LOVE_IT_BANNER}
                alt={t('home.loveItBannerAlt')}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Problem — distinct stat-strip texture */}
      <section className="py-24 md:py-32 px-6 sm:px-10 bg-[var(--bg-surface)] border-y border-[var(--border-strong)]">
        <div className="container-wide">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-center mb-6">
              {t('home.problemTitle')}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-lg text-[var(--text-secondary)] text-center max-w-2xl mx-auto mb-16">
              {t('home.problemText')}
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { value: 70, decimals: 0, suffix: '%', label: t('home.forgotten') },
              { value: 40, decimals: 0, suffix: '%', label: t('home.incomplete') },
              { value: 2.5, decimals: 1, suffix: 'hrs', label: t('home.lostWeekly') },
            ].map((item, index) => (
              <Reveal key={index} delay={0.15 + index * 0.1}>
                <div className="glass-card rounded-2xl p-8 text-center h-full" data-testid={`problem-stat-${index}`}>
                  <CountUpStat
                    value={item.value}
                    decimals={item.decimals}
                    suffix={item.suffix}
                    className="text-5xl sm:text-6xl font-semibold tracking-tight text-[var(--text-primary)] mb-3"
                  />
                  <p className="text-[var(--text-secondary)]">{item.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ScribAI intro / gateway — centered text */}
      <section className="py-24 md:py-32 px-6 sm:px-10 bg-[var(--bg-page)]">
        <div className="container-wide max-w-2xl mx-auto text-center">
          <Reveal>
            <p className="label-small mb-4">{t('home.introducingScribai')}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
              ScribAI
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg text-[var(--accent-link)] mb-4">{t('home.scribaiSubtitle')}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-10">
              {t('home.scribaiDescription')}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/product"
                data-testid="cta-buy"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium bg-[var(--accent-link)] text-white hover:bg-[var(--accent-link-hover)] transition-colors"
              >
                {t('home.buyNow')} <ArrowRight size={16} />
              </Link>
              <Link
                to="/experience"
                data-testid="cta-learn-more"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium border border-[var(--border-strong)] text-[var(--text-primary)] hover:border-[var(--accent-link)] transition-colors"
              >
                {t('home.learnMore')}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Smart Summaries — real animated section, not a flat image */}
      <section className="py-24 md:py-32 px-6 sm:px-10 bg-[var(--bg-surface)] border-y border-[var(--border-strong)]">
        <div className="container-wide">
          <SmartSummariesSection />
        </div>
      </section>
    </div>
  );
};

export default BrandIntroPage;
